'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Link from 'next/link';
import classNames from 'classnames';

import { GothamBold } from '@/utils/fonts';
import { TMenuLink, TMenuLinks, TSubMenu } from './types';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import Icon from '@/components/Icon/Icon';

const MegaMenu = ({ links, headerId }: { links: TMenuLinks[]; headerId?: string }) => {
    const inspectorProps = useContentfulInspectorMode({ entryId: headerId });

    if (!links || !links.length) {
        return null;
    }

    const handleMenuClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement;

        const linkElement = target.closest('a') as HTMLAnchorElement;
        const topLevelElement = target.closest('.parent-menu-item');

        if (!linkElement && !topLevelElement) return;

        if (topLevelElement && !topLevelElement.classList.contains('tw-text-pink1')) {
            const navLinkText = topLevelElement.textContent?.trim() || '';
            const navLinkUrl = linkElement ? linkElement.href : null;

            window.dataLayer &&
                window.dataLayer.push({
                    event: 'main_nav_click',
                    nav_link_text: navLinkText,
                    ...(navLinkUrl ? { nav_link_url: navLinkUrl } : {}),
                });
            window.dataLayer &&
                window.dataLayer.push({ event: 'cta_click', cta_type: 'navigation click' });
        } else if (linkElement) {
            const navLinkText = linkElement.textContent?.trim() || '';
            const navLinkUrl = linkElement.href || '';

            window.dataLayer &&
                window.dataLayer.push({
                    event: 'sub_nav_click',
                    nav_link_text: navLinkText,
                    nav_link_url: navLinkUrl,
                });
            window.dataLayer &&
                window.dataLayer.push({ event: 'cta_click', cta_type: 'navigation click' });
        }
    };

    return (
        <div
            className="tw-hidden lg:!tw-flex lg:!tw-visible tw-py-[6px] tw-items-center"
            {...inspectorProps({ fieldId: 'title' })}
            onClick={handleMenuClick}>
            {links.map((item, index) => (
                <ParentMenuItem key={index} item={item} />
            ))}
        </div>
    );
};

const ParentMenuItem = ({ item: { title, href, subMenu, id } }: { item: TMenuLinks }) => {
    const inspectorProps = useContentfulInspectorMode({ entryId: id });

    const [isOpen, setIsOpen] = useState(false);
    const [curPosition, setCurPosition] = useState<DOMRect>();
    const menuMainItemRef = useRef<HTMLDivElement>(null);
    const menuContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!menuMainItemRef.current) {
            return;
        }

        setCurPosition(menuMainItemRef.current?.getBoundingClientRect());
    }, [menuMainItemRef]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                isOpen &&
                menuContainerRef.current &&
                !menuContainerRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [setIsOpen, isOpen, menuContainerRef]);

    // Hide the menu when resizing the window
    useEffect(() => {
        const handleResize = () => {
            if (isOpen) {
                setIsOpen(false);
            }
        };
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [isOpen]);

    if (subMenu) {
        return (
            <div ref={menuContainerRef}>
                <div
                    ref={menuMainItemRef}
                    className={classNames(
                        'parent-menu-item tw-flex tw-flex-row tw-text-[13px] tw-items-center tw-gap-1 tw-transition-all tw-duration-200 tw-ease-in-out hover:tw-text-pink1 tw-px-[7px] tw-cursor-pointer tw-text-nowrap tw-group',
                        { 'tw-text-pink1': isOpen },
                    )}
                    onClick={() => setIsOpen((prevState) => !prevState)}
                    {...inspectorProps({
                        fieldId: 'title',
                    })}>
                    <span>{title}</span>
                    <Icon
                        type="angleRight"
                        width={18}
                        height={18}
                        className="tw-fill-menuText tw-rotate-90 tw-transition-all tw-duration-200 group-hover:tw-fill-pink1"
                    />
                </div>
                <MenuSubItemsContainer
                    mainItemCurPosition={curPosition}
                    items={subMenu}
                    columnProps={inspectorProps}
                    isOpen={isOpen}
                />
            </div>
        );
    }

    return (
        <Link
            href={href}
            prefetch={false}
            className="parent-menu-item tw-transition-all tw-duration-200 tw-ease-in-out tw-text-[13px] hover:tw-no-underline hover:tw-text-pink1 tw-px-[7px] tw-cursor-pointer"
            {...inspectorProps({ fieldId: 'title' })}>
            {title}
        </Link>
    );
};

const MenuSubItemsContainer = ({
    items,
    isOpen,
    mainItemCurPosition,
    columnProps,
}: {
    isOpen: boolean;
    items: TSubMenu[];
    mainItemCurPosition?: DOMRect;
    columnProps: unknown;
}) => {
    const subMenuRef = useRef<HTMLDivElement>(null);
    const menuPositionRef = useRef<HTMLSpanElement>(null);
    const [popupRect, setPopupRect] = useState<DOMRect>();
    const [menuOffset, setMenuOffset] = useState(0);
    const [isFinishedAnimating, setIsFinishedAnimating] = useState<boolean | undefined>(true);
    const [containerRef, setContainerRef] = useState<HTMLElement | null>(null);
    const debounceTimeout = useRef<any>(null);

    const prevIsOpenRef = useRef<boolean>();

    const recalculatePosition = () => {
        if (!mainItemCurPosition && menuPositionRef.current) {
            const currentPosition = menuPositionRef.current.getBoundingClientRect();
            mainItemCurPosition = currentPosition;
        }

        if (subMenuRef.current) {
            const rect = subMenuRef.current.getBoundingClientRect();
            setPopupRect(rect);
        }
        if (menuPositionRef.current) {
            const offset = menuPositionRef.current.getBoundingClientRect().left ?? 0;
            setMenuOffset(offset);
        }
    };

    useEffect(() => {
        if (prevIsOpenRef.current && prevIsOpenRef.current === true && !isOpen) {
            setTimeout(() => {
                setIsFinishedAnimating(true);
            }, 200);
        } else if (
            typeof prevIsOpenRef.current !== 'undefined' &&
            prevIsOpenRef.current !== isOpen
        ) {
            setIsFinishedAnimating(false);
        }
        prevIsOpenRef.current = isOpen;
    }, [isOpen]);

    useEffect(() => {
        if (typeof document !== 'undefined') {
            const container = document.querySelector('.header-container');
            setContainerRef(container as HTMLElement);
        }

        const handleResize = () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }

            debounceTimeout.current = setTimeout(() => {
                recalculatePosition();
            }, 100);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, []);

    useLayoutEffect(() => {
        recalculatePosition();
    }, [isOpen]);

    const containerWidth = mainItemCurPosition?.width ?? 0;
    const centerOfParent = (mainItemCurPosition?.left ?? 0) + containerWidth / 2;

    const containerLeft = containerRef?.getBoundingClientRect().left || 0;
    const containerWidthBoundary = containerRef?.getBoundingClientRect().width || 0;
    const containerRight = containerLeft + containerWidthBoundary;

    let popupLeftPosition = centerOfParent - (popupRect?.width ?? 0) / 2 - menuOffset;

    if (popupRect && popupRect.width < 300) {
        popupLeftPosition = centerOfParent - popupRect.width / 2;
    }

    const isPopupOverflowingLeft = popupLeftPosition < containerLeft;
    const isPopupOverflowingRight = popupLeftPosition + (popupRect?.width ?? 0) > containerRight;

    if (isPopupOverflowingLeft) {
        popupLeftPosition = containerLeft;
    } else if (isPopupOverflowingRight) {
        popupLeftPosition = containerRight - (popupRect?.width ?? 0);
    }

    return (
        <>
            <span
                className="tw-absolute tw-w-0 tw-h-0 tw-left-0 tw-top-0"
                ref={menuPositionRef}></span>
            <span
                className={classNames(
                    "tw-transition tw-ease-in-out tw-duration-200 tw-absolute tw-z-10 before:tw-z-10 before:tw-mt-1 before:tw-border-t-0 before:tw-drop-shadow-menuArrow before:-tw-translate-y-1/2 before:tw-border-[15px] before:tw-border-solid before:tw-border-b-white before:tw-border-l-transparent before:tw-border-r-transparent before:tw-border-t-transparent before:tw-contents-[''] -tw-translate-x-1/2 before:tw-w-[20px] before:tw-h-[20px] before:!tw-block tw-w-5 tw-h-5",
                    {
                        'tw-opacity-100': isOpen,
                        'tw-opacity-0': !isOpen,
                        'tw-top-[-1000px]': isFinishedAnimating,
                    },
                )}
                style={{
                    marginLeft: containerWidth / 2,
                }}
            />
            <div
                className={classNames(
                    'tw-transition tw-ease-in-out tw-duration-200 tw-absolute tw-max-w-100 tw-bg-white tw-px-9 tw-pt-2 tw-pb-4 tw-shadow-menu tw-rounded-md',
                    {
                        'tw-opacity-100': isOpen,
                        'tw-opacity-0': !isOpen,
                        'tw-top-[-1000px]': isFinishedAnimating,
                    },
                )}
                style={
                    !isFinishedAnimating
                        ? {
                              top: (mainItemCurPosition?.bottom ?? 0) + 12,
                              left: `${popupLeftPosition}px`,
                          }
                        : {}
                }
                ref={subMenuRef}>
                <div
                    className={classNames('tw-flex tw-gap-7', {
                        'tw-flex-row': items.length > 1,
                        'tw-flex-col': items.length === 1,
                    })}>
                    <SubItems items={items} columnProps={columnProps} />
                </div>
            </div>
        </>
    );
};

const SubItems = ({ items, columnProps }: { items: TSubMenu[]; columnProps: any }) => {
    if (!items) {
        return null;
    }

    if (items.length === 1) {
        return <MenuSingleColumn column={items[0]} columnProps={columnProps} />;
    }

    return items.map((column, columnIndex) => {
        return <MenuMultipleColumns key={columnIndex} column={column} />;
    });
};

const MenuSingleColumn = ({ column, columnProps }: { column: TSubMenu; columnProps: any }) => {
    return (
        <div {...columnProps({ fieldId: 'title' })}>
            {column.map((item) => (
                <MenuSingleColumnItem item={item} key={item.id} />
            ))}
        </div>
    );
};

const MenuSingleColumnItem = ({ item }: { item: TMenuLink }) => {
    const inspectorProps = useContentfulInspectorMode({ entryId: item.id });

    return (
        <div className="tw-px-[7px] tw-first:pt-6 tw-last:pb-5 tw-pt-[11px] tw-max-w-[200px]">
            <MenuItem
                item={item}
                className="tw-transition-all tw-duration-200 tw-ease-in-out hover:tw-no-underline tw-border-b-2 tw-border-transparent hover:tw-border-pink1 hover:tw-text-pink1"
                inspectorProps={inspectorProps({ fieldId: 'title' })}
            />
        </div>
    );
};

const MenuMultipleColumns = ({ column }: { column: TSubMenu }) => {
    const inspectorProps = useContentfulInspectorMode({ entryId: column[0].parentId });

    return (
        <div className="tw-flex tw-flex-col tw-w-[177px]" {...inspectorProps({ fieldId: 'title' })}>
            {column.map((item, index) => {
                return <MenuMultipleColumnsItem item={item} key={item.id} />;
            })}
        </div>
    );
};

const MenuMultipleColumnsItem = ({ item }: { item: TMenuLink }) => {
    const inspectorProps = useContentfulInspectorMode({ entryId: item.id });

    return (
        <div
            className={classNames({
                'tw-mx-[9px] tw-mt-[20px] tw-min-h-[49px]': !!item.isHeader,
                'tw-py-1 tw-px-[10px]': !item.isHeader,
            })}>
            <MenuItem
                item={item}
                className={classNames(
                    'tw-transition-all tw-duration-200 tw-ease-in-out tw-text-xs tw-border-b-2 tw-border-transparent',
                    '[&:is(a):hover]:tw-no-underline [&:is(a):hover]:tw-border-pink1 hover:tw-text-menuLinkHover ',
                    {
                        [GothamBold.className]: !!item.isHeader,
                        'tw-text-megaMenuHeaderLink tw-leading-[18px] !tw-text-[14px]':
                            !!item.isHeader,
                        'tw-text-megaMenuLink': !item.isHeader,
                    },
                )}
                inspectorProps={inspectorProps({ fieldId: 'title' })}
            />
        </div>
    );
};

const MenuItem = ({
    item: { href, isHeader, target, title },
    className,
    inspectorProps,
}: {
    item: TMenuLink;
    className: string;
    inspectorProps: any;
}) => {
    if (target === '_blank') {
        return (
            <a href={href} target={target} className={className} {...inspectorProps}>
                {title}
            </a>
        );
    }

    if (/^\/[a-z]{2}\/clienthub\/?$/i.test(href)) {
        return (
            <div className={className} {...inspectorProps}>
                {title}
            </div>
        );
    }

    return (
        <Link href={href} prefetch={null} className={className} {...inspectorProps}>
            {title}
        </Link>
    );
};

export default MegaMenu;
